import React, { useState } from 'react';
import { X, User, Phone, Mail, MapPin, CheckCircle2, ShieldCheck, AlertCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { LocationSelector } from '../common/LocationSelector';
import { ProfilePhotoUploader } from '../common/ProfilePhotoUploader';
import { isValidLgaForState } from '../../data/nigerianLocations';

interface CustomerProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CustomerProfileModal: React.FC<CustomerProfileModalProps> = ({ isOpen, onClose }) => {
  const { currentUser, updateCustomerProfile } = useApp();

  const [avatarUrl, setAvatarUrl] = useState(currentUser?.avatarUrl || '');
  const [fullName, setFullName] = useState(currentUser?.fullName || '');
  const [phone, setPhone] = useState(currentUser?.phone || '');
  const [state, setState] = useState(currentUser?.state || 'Lagos');
  const [lga, setLga] = useState(currentUser?.lga || 'Ikeja');
  const [cityArea, setCityArea] = useState(currentUser?.cityArea || '');
  const [address, setAddress] = useState(currentUser?.address || '');

  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  if (!isOpen) return null;

  // Calculate local profile completion
  const isComplete = Boolean(
    fullName.trim() &&
    phone.trim() &&
    state.trim() &&
    lga.trim() &&
    address.trim()
  );
  let completionPct = 20; // email already present
  if (fullName.trim()) completionPct += 20;
  if (phone.trim()) completionPct += 20;
  if (state.trim() && lga.trim()) completionPct += 20;
  if (address.trim()) completionPct += 20;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!fullName.trim()) {
      setErrorMessage('Full name is required.');
      return;
    }
    if (!phone.trim()) {
      setErrorMessage('Nigerian phone number is required.');
      return;
    }
    if (!isValidLgaForState(state, lga)) {
      setErrorMessage(`Selected LGA '${lga}' is not valid for '${state}'. Please select a valid LGA.`);
      return;
    }

    setIsSaving(true);
    try {
      await updateCustomerProfile({
        fullName: fullName.trim(),
        avatarUrl: avatarUrl ? avatarUrl.trim() : undefined,
        phone: phone.trim(),
        state: state.trim(),
        lga: lga.trim(),
        cityArea: cityArea.trim(),
        address: address.trim(),
      });
      setSuccessMessage('Profile details saved successfully.');
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (err: any) {
      setErrorMessage(err?.message || 'Failed to update profile.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs animate-fade-in">
      <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-slate-200 relative max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
            <User className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-slate-900">Customer Profile & Address</h2>
            <p className="text-xs text-slate-500">
              Manage your personal contact details, location, and preferred service address.
            </p>
          </div>
        </div>

        {/* Status & Profile Completion Badges */}
        <div className="mb-6 p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-bold text-slate-700 flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span>Profile Completion</span>
            </span>
            <span className="font-extrabold text-emerald-700">{completionPct}% Complete</span>
          </div>

          <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
            <div
              className="bg-emerald-600 h-full transition-all duration-300"
              style={{ width: `${completionPct}%` }}
            />
          </div>

          <div className="flex items-center justify-between pt-1 text-[11px] text-slate-500">
            <span>Account Status: <strong className="text-emerald-700 uppercase font-bold">Active</strong></span>
            <span>Phone: {currentUser?.phoneVerified ? <span className="text-emerald-700 font-semibold">✓ Verified</span> : 'Registered'}</span>
          </div>
        </div>

        {errorMessage && (
          <div className="mb-4 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>{successMessage}</span>
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-4 text-left">
          {/* Profile Photo Uploader */}
          <ProfilePhotoUploader
            photoUrl={avatarUrl}
            onChange={setAvatarUrl}
            userRole="customer"
            label="Customer Profile Photo"
          />

          {/* Full Name */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Full Name
            </label>
            <div className="relative">
              <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="e.g. Folashade Balogun"
                className="w-full pl-10 pr-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:outline-hidden focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20"
                required
              />
            </div>
          </div>

          {/* Email (Read only authenticated identity) */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Email Address (Account Identity)
            </label>
            <div className="relative">
              <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
              <input
                type="email"
                value={currentUser?.email || ''}
                disabled
                className="w-full pl-10 pr-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-100 text-slate-500 text-xs font-medium cursor-not-allowed"
              />
            </div>
            <span className="text-[10px] text-slate-400 mt-1 block">Connected to your JobMatch NG credentials</span>
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Phone Number
            </label>
            <div className="relative">
              <Phone className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="e.g. 0802 345 6789"
                className="w-full pl-10 pr-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:outline-hidden focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20"
                required
              />
            </div>
          </div>

          {/* Nigerian Location: State and LGA Selection */}
          <div className="pt-2 border-t border-slate-100">
            <LocationSelector
              idPrefix="cust-edit"
              selectedState={state}
              selectedLga={lga}
              selectedCityArea={cityArea}
              onStateChange={(newState) => setState(newState)}
              onLgaChange={(newLga) => setLga(newLga)}
              onCityAreaChange={(newArea) => setCityArea(newArea)}
              theme="light"
              layout="stacked"
              showCountry={false}
              showCityArea={true}
            />
          </div>

          {/* Street / Residential Address */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Street / Residential Address
            </label>
            <div className="relative">
              <MapPin className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="e.g. 18 Isaac John Street, Ikeja GRA"
                className="w-full pl-10 pr-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:outline-hidden focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20"
                required
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-slate-600 hover:text-slate-900 font-bold text-xs transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
            >
              {isSaving ? (
                <span>Saving Changes...</span>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Save Profile</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
